import { contentLinkNodeNavigateTo } from 'src/modules/dashboard/helpers/contentLinkNodeNavigateTo';
import { ContentBannerProps } from '@modernhealth/vital-components';
import i18n from 'src/config/i18n';
import { ContentLinkFragment } from '../documents/getHomeScreen.generated';
import { screenLinkToPath } from './contentLinkNodeToSectionHeader';
import { getTrackClickCallback, ContentTracking } from './getTrackClickCallback';

type MapContentLinkToContentBannerProps = {
    link: ContentLinkFragment;
    tracking?: ContentTracking;
};

const DescriptionStartIcon = ({ src }: { src?: string }) => {
    if (!src) return null;
    return <svg data-src={src} aria-hidden="true" width="100%" height="100%" />;
};

export const mapContentLinkToContentBannerProps = ({
    link,
    tracking,
}: MapContentLinkToContentBannerProps): ContentBannerProps | null => {
    const trackClickCallback = buttonCopy =>
        tracking
            ? getTrackClickCallback({
                  link,
                  ...tracking,
                  props: { button_copy: buttonCopy, ...tracking.props },
              })
            : undefined;

    switch (link.__typename) {
        case 'MeditationLinkNode':
            return link.meditation
                ? {
                      title: link.meditation.name,
                      buttonTitle:
                          link.meditation.description ?? i18n.t('shared:banner_button.listen'),
                      buttonProps: { variant: 'flat' },
                      description: i18n.t(
                          link.mediaType?.name
                              ? `shared:media_type.duration_with_min`
                              : 'dashboard:meditation.duration',
                          {
                              duration: link.meditation.duration ?? 1,
                              mediaTypeName: link.mediaType?.name,
                          },
                      ),
                      backgroundColor: link.banner?.backgroundColor,
                      image: link.banner?.imageUrl ?? link.meditation.image ?? '',
                      descriptionStartIcon: <DescriptionStartIcon src={link.mediaType?.icon} />,
                      trackClickCallback: trackClickCallback(
                          link.meditation.description ?? i18n.t('shared:banner_button.listen'),
                      ),
                      ...contentLinkNodeNavigateTo(link),
                  }
                : null;
        case 'ProgramLinkNode':
            return link.program
                ? {
                      title: link.program.name,
                      buttonTitle: i18n.t('shared:banner_button.learn_now'),
                      buttonProps: { variant: 'flat' },
                      description: i18n.t(
                          link.mediaType?.name
                              ? `shared:media_type.duration_with_min`
                              : 'dashboard:program.duration',
                          {
                              duration: link.program.duration ?? 1,
                              mediaTypeName: link.mediaType?.name,
                          },
                      ),
                      backgroundColor: link.banner?.backgroundColor,
                      image: link.banner?.imageUrl ?? link.program.image ?? '',
                      descriptionStartIcon: <DescriptionStartIcon src={link.mediaType?.icon} />,
                      trackClickCallback: trackClickCallback(
                          i18n.t('shared:banner_button.learn_now'),
                      ),
                      ...contentLinkNodeNavigateTo(link),
                  }
                : null;
        case 'ExternalLinkNode':
            return {
                title: link.title,
                description: '',
                buttonTitle: link.subtitle ?? '',
                buttonProps: { variant: 'flat' },
                image: link.image ?? '',
                trackClickCallback: trackClickCallback(link.subtitle ?? ''),
                ...contentLinkNodeNavigateTo(link),
            };
        case 'InternalLinkNode':
            return {
                title: link.title,
                description: '',
                buttonTitle: link.subtitle ?? '',
                buttonProps: { variant: 'flat' },
                image: link.image ?? '',
                trackClickCallback: trackClickCallback(link.subtitle ?? ''),
                ...contentLinkNodeNavigateTo(link),
            };
        // Circles links aren't supported in content banners (yet)
        case 'CircleSeriesLinkNode':
        case 'CircleSessionLinkNode':
        case 'CircleTopicLinkNode':
            return null;
        case 'ScreenLinkNode':
            const path = screenLinkToPath(link.screen);

            return path
                ? {
                      title: link.title,
                      image: link.image ?? '',
                      description: '',
                      buttonTitle: link.subtitle ?? '',
                      buttonProps: { variant: 'flat' },
                      linkTo: path,
                      trackClickCallback: trackClickCallback(link.subtitle ?? ''),
                  }
                : null;
        case 'CourseLinkNode':
            return {
                title: link.course.title,
                buttonTitle: i18n.t('shared:banner_button.learn_now'),
                buttonProps: { variant: 'flat' },
                description: i18n.t(
                    link.mediaType?.name
                        ? `shared:media_type.duration_with_min`
                        : 'courses:course.duration',
                    {
                        duration: link.course.duration ?? 1,
                        mediaTypeName: link.mediaType?.name,
                    },
                ),
                backgroundColor: link.banner?.backgroundColor,
                image: link.banner?.imageUrl ?? '',
                descriptionStartIcon: <DescriptionStartIcon src={link.mediaType?.icon} />,
                trackClickCallback: trackClickCallback(i18n.t('shared:banner_button.learn_now')),
                ...contentLinkNodeNavigateTo(link),
            };
        default:
            return null;
    }
};
